import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import {
  BUY, SELL,
  SectionContainer, SectionHeader, SectionWrapper,
  UNIT_CARBON, UNIT_CARBON_DEFAULT_LABEL,
  UNIT_CURRENCY, UNIT_ENERGY, UNIT_ENERGY_LABEL,
} from 'src/enosikit/components/Chart/components/chartConstants';
import { extractTradeValues, getTooltipFormatFunc } from 'src/enosikit/components/Chart/helpers/tooltip';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
} from 'src/util/constants';
import getMeterLabel from 'src/util/meter';

import { getEnergyOriginLabel } from './propertyShowChart';

/**
 * Build the meter label and header for the tooltip
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {BUY | SELL} tradeDirection - BUY | SELL
 * @param {string} title - meter title
 * @param {string} identifier - meter identifier
 * @returns {string} - meter label.
 */
const buildTooltipMeterHeader = (intl, tradeDirection, title, identifier) => {
  const importHeader = intl.formatMessage({ id: 'property.property_show.helpers.chart_tooltip.imports.header.label', defaultMessage: 'Imports' });
  const exportHeader = intl.formatMessage({ id: 'property.property_show.helpers.chart_tooltip.exports.header.label', defaultMessage: 'Exports' });
  const header = tradeDirection === BUY ? importHeader : exportHeader;
  const meterDetails = getMeterLabel(identifier, title);
  const label = meterDetails ? `${header} - ${meterDetails}` : header;
  return label;
};

/**
 * Renders the tooltip content for trade chart
 * @param {object} data - the tooltip datapack.
 * @param {string} unitLabel
 * @param {object} chartView - { aggregateBy, groupBy }
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_METER} chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 * @param {UNIT_CURRENCY | UNIT_ENERGY | UNIT_CARBON} unit - chart unit
 * @returns {React.ReactElement | null} - trade tooltip.
 */
export const tradeChartTooltip = (
  data,
  unitLabel,
  chartView,
  unit,
) => {
  if (!data || !chartView) {
    return null;
  }
  const intl = useIntl();

  const values = extractTradeValues(data);
  const tooltipFormatFunc = getTooltipFormatFunc(unit, values);

  const tradeTooltip = [BUY, SELL].map((direction) => {
    if (!data[direction] || Object.keys(data[direction]).length === 0) {
      return null;
    }
    const tooltipHeader = direction === BUY ? intl.formatMessage({ id: 'property.property_show.chart_tooltip.trade_chart_tooltip.import.header.label', defaultMessage: 'Imports' }) : intl.formatMessage({ id: 'property.property_show.chart_tooltip.trade_chart_tooltip.export.header.label', defaultMessage: 'Exports' });

    const { aggregateBy } = chartView;
    const isByMeter = aggregateBy === DATA_AGGREGATE_BY_METER;

    return (
      <SectionWrapper key={direction}>
        {!isByMeter && (
          <SectionHeader>
            {tooltipHeader}
          </SectionHeader>
        )}

        {Object.keys(data[direction]).map((key) => {
          const d = data[direction][key];
          if (!d) {
            return null;
          }
          const {
            label, meter, subLabel, value,
          } = d;

          const { title, identifier } = meter || {};
          const meterLabel = buildTooltipMeterHeader(intl, direction, title, identifier);
          const energyOriginLabel = getEnergyOriginLabel(intl, label, chartView);

          return (
            <Fragment key={key}>
              {(isByMeter)
                && (
                  <SectionHeader>
                    {meterLabel}
                  </SectionHeader>
                )}
              <Row key={key} className="mb-1">
                <Col className="pe-0">
                  <span>{energyOriginLabel}</span>
                  {subLabel && (
                    <>
                      <br />
                      <small>
                        {`(${subLabel})`}
                      </small>
                    </>
                  )}
                </Col>
                <Col xs="auto">
                  {tooltipFormatFunc(value, unitLabel)}
                </Col>
              </Row>

            </Fragment>
          );
        })}
      </SectionWrapper>
    );
  });
  return tradeTooltip;
};

/**
 * Forms the content for the meter tooltip
 * @param {object} tooltipData
 * @param {BUY|SELL} label
 * @param {string} unitLabel
 * @param {import('react-intl').IntlShape} intl
 * @param {Function} tooltipFormatFunc
 * @returns {React.ReactElement} - meter tooltip content.
 */
const meterContent = (
  tooltipData,
  label,
  unitLabel,
  intl,
  tooltipFormatFunc,
) => {
  const {
    flags, value, title, identifier,
  } = tooltipData || {};

  return (
    <SectionWrapper>
      <SectionHeader>
        {buildTooltipMeterHeader(intl, label, title, identifier)}
      </SectionHeader>
      <div>
        {tooltipFormatFunc(value, unitLabel)}
      </div>
      {flags?.length > 0 && (
        <SectionContainer>
          {flags.map((el) => el.identifier).join(', ')}
        </SectionContainer>
      )}
    </SectionWrapper>
  );
};

/**
 * Renders the details for a meter in a meter chart tooltip.
 * @param {object} tooltipData - The data to be displayed in the meter tooltip.
 * @param {string} label - The label for the meter.
 * @param {UNIT_CARBON_DEFAULT_LABEL | UNIT_ENERGY_LABEL | string} unitLabel - The label for
 * the unit of the data.
 * @param {boolean} isByMeter - Indicates whether the chart is aggregated by meter.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {Function} tooltipFormatFunc
 * @returns {React.ReactElement | null} - The rendered meter details.
 */
const renderMeterDetails = (
  tooltipData,
  label,
  unitLabel,
  isByMeter,
  intl,
  tooltipFormatFunc,
) => {
  if (!isByMeter) {
    return meterContent(
      tooltipData,
      label,
      unitLabel,
      intl,
      tooltipFormatFunc,
    );
  }

  const meterDetails = Object.keys(tooltipData).map((meterId) => (
    <Fragment key={meterId}>
      {meterContent(
        tooltipData[meterId],
        label,
        unitLabel,
        intl,
        tooltipFormatFunc,
      )}
    </Fragment>
  ));
  return meterDetails;
};

/**
 * Renders the tooltip content for a meter chart.
 * @param {object} tooltipData - The data to be displayed in the tooltip.
 * @param {UNIT_CARBON_DEFAULT_LABEL | UNIT_ENERGY_LABEL | string} unitLabel - The label for
 * the unit of the data.
 * @param {object} chartView - { aggregateBy, groupBy }
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_METER} chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 * @param {UNIT_CURRENCY | UNIT_CARBON |  UNIT_ENERGY} unit
 * @returns {React.ReactElement | null} - The rendered tooltip content,
 */
export const meterChartTooltip = (
  tooltipData,
  unitLabel,
  chartView,
  unit,
) => {
  if (!tooltipData || !chartView) {
    return null;
  }
  const { tooltipConsumed, tooltipGenerated } = tooltipData;
  if (!tooltipConsumed && !tooltipGenerated) {
    return null;
  }
  const intl = useIntl();
  const { aggregateBy } = chartView;
  const isByMeter = aggregateBy === DATA_AGGREGATE_BY_METER;
  const tooltipFormatFunc = getTooltipFormatFunc(unit);

  const tooltips = [
    {
      key: 'tooltip-consumed',
      tooltip: (tooltipConsumed
        && (renderMeterDetails(
          tooltipConsumed,
          BUY,
          unitLabel,
          isByMeter,
          intl,
          tooltipFormatFunc,
        ))
      ),
    }, {
      key: 'tooltip-generated',
      tooltip: (tooltipGenerated
        && (renderMeterDetails(
          tooltipGenerated,
          SELL,
          unitLabel,
          isByMeter,
          intl,
          tooltipFormatFunc,
        ))),
    },
  ];

  return (
    tooltips.map((tt) => (
      <Fragment key={tt.key}>
        {tt.tooltip}
      </Fragment>
    ))
  );
};
